import cn from 'clsx';

import {ProgressTrack, TRACK_TRANSITION, trackRatio, useProgressReveal} from '@/components/ProgressTrack';
import {typographyVariants} from '@/components/Typography';
import experienceScoreStatuses, {isPositiveStatus} from '@/consts/experienceScore/experienceScoreStatuses';
import {interpolate} from '@/services/localization/localizationService';
import type {ProgressBarModel} from '@/types/aem';

import useValueLabelLayout from './useValueLabelLayout';

interface MetricBarProps {
    progressBar?: ProgressBarModel;
    value: number;
    /** Scale end reported for this consumer - falls back to the authored example. */
    max?: number;
    status?: string;
    labelledBy?: string;
    className?: string;
}

const MetricBar = ({
    progressBar,
    value,
    max,
    status = experienceScoreStatuses.OPTIMAL,
    labelledBy,
    className,
}: MetricBarProps) => {
    const {maxValue = 0, valueFormat = '', minLabel, valueLabel, maxLabel} = progressBar ?? {};
    const barMax = max ?? maxValue;
    const ratio = useProgressReveal(trackRatio(value, barMax));
    const {rowRef, valueRef, minRef, maxRef, left, isMinHidden, isMaxHidden} = useValueLabelLayout(ratio);

    if (!progressBar) return null;

    const format = (barValue: number) => interpolate(valueFormat, Math.round(barValue));

    return (
        <div className={cn('flex w-full flex-col gap-2', className)}>
            <ProgressTrack
                ratio={ratio}
                value={value}
                max={barMax}
                isPositive={isPositiveStatus(status)}
                labelledBy={labelledBy}
            >
                {format(value)}
            </ProgressTrack>
            <div ref={rowRef} className={cn('relative h-5', typographyVariants.bodySmallBold)}>
                <span ref={minRef} className={cn('absolute left-0', isMinHidden && 'invisible')}>
                    {minLabel}
                </span>
                {valueLabel && (
                    <span ref={valueRef} style={{left}} className={cn('absolute', TRACK_TRANSITION)}>
                        {valueLabel}
                    </span>
                )}
                <span ref={maxRef} className={cn('absolute right-0', isMaxHidden && 'invisible')}>
                    {max === undefined ? maxLabel : format(max)}
                </span>
            </div>
        </div>
    );
};

export default MetricBar;
